
import { useState, useEffect } from 'react'
import { usePageSections, useUpdatePageSection, useSiteConfig, useCMSComponents } from '@/hooks/useCMS'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { 
  Eye, 
  Smartphone, 
  Tablet, 
  Monitor, 
  Plus, 
  Save, 
  Palette, 
  Type, 
  Layout,
  Image as ImageIcon,
  MousePointer,
  Layers
} from 'lucide-react'

interface CMSVisualEditorProps {
  pageId: string
  previewMode?: 'desktop' | 'tablet' | 'mobile'
}

export const CMSVisualEditor = ({ pageId, previewMode = 'desktop' }: CMSVisualEditorProps) => {
  const { data: sections, isLoading } = usePageSections(pageId)
  const { data: config } = useSiteConfig()
  const { data: components } = useCMSComponents()
  const updateSection = useUpdatePageSection()
  
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('content')
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>(previewMode)
  
  const [sectionData, setSectionData] = useState<any>({})

  useEffect(() => {
    if (sections && sections.length > 0 && !selectedSection) {
      setSelectedSection(sections[0].id)
    }
  }, [sections, selectedSection])

  const currentSection = sections?.find(s => s.id === selectedSection)

  const handleSectionUpdate = async (field: string, value: any) => {
    if (!currentSection) return

    const updates = { [field]: value }
    setSectionData(prev => ({ ...prev, [field]: value }))

    try {
      await updateSection.mutateAsync({
        id: currentSection.id,
        ...updates
      })
      toast.success('Section mise à jour')
    } catch (error) {
      toast.error('Erreur lors de la mise à jour')
      console.error(error)
    }
  }

  const handleStyleUpdate = async (property: string, value: any) => {
    if (!currentSection) return

    const updatedStyles = {
      ...currentSection.styles,
      [property]: value
    }

    handleSectionUpdate('styles', updatedStyles)
  }

  const handleResponsiveUpdate = async (device: string, property: string, value: any) => {
    if (!currentSection) return

    const updatedResponsive = {
      ...currentSection.responsive_settings,
      [device]: {
        ...currentSection.responsive_settings?.[device],
        [property]: value
      }
    }

    handleSectionUpdate('responsive_settings', updatedResponsive)
  }

  const getViewportClasses = () => {
    switch (viewport) {
      case 'mobile':
        return 'w-[375px] h-[667px]'
      case 'tablet':
        return 'w-[768px] h-[1024px]'
      default:
        return 'w-full h-full'
    }
  }

  if (isLoading) {
    return <div>Chargement de l'éditeur...</div>
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Panneau de configuration */}
      <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Éditeur Visuel</h3>
          
          {/* Sélecteur de section */}
          <div className="mb-4">
            <Label>Section à éditer</Label>
            <Select value={selectedSection || ''} onValueChange={setSelectedSection}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une section" />
              </SelectTrigger>
              <SelectContent>
                {sections?.map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {section.title || section.section_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {currentSection && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="content">
                  <Type className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="design">
                  <Palette className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="layout">
                  <Layout className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="effects">
                  <MousePointer className="w-4 h-4" />
                </TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="space-y-4">
                <div>
                  <Label>Titre</Label>
                  <Input
                    value={currentSection.title || ''}
                    onChange={(e) => handleSectionUpdate('title', e.target.value)}
                    placeholder="Titre de la section"
                  />
                </div>
                
                <div>
                  <Label>Sous-titre</Label>
                  <Input
                    value={currentSection.subtitle || ''}
                    onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
                    placeholder="Sous-titre"
                  />
                </div>
                
                <div>
                  <Label>Contenu</Label>
                  <Textarea
                    value={currentSection.content || ''}
                    onChange={(e) => handleSectionUpdate('content', e.target.value)}
                    placeholder="Contenu de la section"
                    rows={6}
                  />
                </div>

                <div>
                  <Label>Image</Label>
                  <Input
                    value={currentSection.image_url || ''}
                    onChange={(e) => handleSectionUpdate('image_url', e.target.value)}
                    placeholder="URL de l'image"
                  />
                </div>
              </TabsContent>

              <TabsContent value="design" className="space-y-4">
                <div>
                  <Label>Couleur de fond</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentSection.background_color || '#ffffff'}
                      onChange={(e) => handleSectionUpdate('background_color', e.target.value)}
                      className="w-16"
                    />
                    <Input
                      value={currentSection.background_color || '#ffffff'}
                      onChange={(e) => handleSectionUpdate('background_color', e.target.value)}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div>
                  <Label>Couleur du texte</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentSection.text_color || '#000000'}
                      onChange={(e) => handleSectionUpdate('text_color', e.target.value)}
                      className="w-16"
                    />
                    <Input
                      value={currentSection.text_color || '#000000'}
                      onChange={(e) => handleSectionUpdate('text_color', e.target.value)}
                      placeholder="#000000"
                    />
                  </div>
                </div>

                <div>
                  <Label>Taille de police</Label>
                  <Slider
                    value={[parseInt(currentSection.styles?.fontSize || '16')]}
                    onValueChange={([value]) => handleStyleUpdate('fontSize', `${value}px`)}
                    min={12}
                    max={48}
                    step={1}
                  />
                  <span className="text-sm text-gray-500">
                    {currentSection.styles?.fontSize || '16px'}
                  </span>
                </div>

                <div>
                  <Label>Police</Label>
                  <Select 
                    value={currentSection.styles?.fontFamily || 'Inter'}
                    onValueChange={(value) => handleStyleUpdate('fontFamily', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter</SelectItem>
                      <SelectItem value="Roboto">Roboto</SelectItem>
                      <SelectItem value="Poppins">Poppins</SelectItem>
                      <SelectItem value="Montserrat">Montserrat</SelectItem>
                      <SelectItem value="Open Sans">Open Sans</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="layout" className="space-y-4">
                <div>
                  <Label>Padding (px)</Label>
                  <Slider
                    value={[parseInt(currentSection.styles?.padding || '16')]}
                    onValueChange={([value]) => handleStyleUpdate('padding', `${value}px`)}
                    min={0}
                    max={100}
                    step={4}
                  />
                  <span className="text-sm text-gray-500">
                    {currentSection.styles?.padding || '16px'}
                  </span>
                </div>

                <div>
                  <Label>Margin (px)</Label>
                  <Slider
                    value={[parseInt(currentSection.styles?.margin || '0')]}
                    onValueChange={([value]) => handleStyleUpdate('margin', `${value}px`)}
                    min={0}
                    max={100}
                    step={4}
                  />
                  <span className="text-sm text-gray-500">
                    {currentSection.styles?.margin || '0px'}
                  </span>
                </div>

                <div>
                  <Label>Largeur maximale</Label>
                  <Select 
                    value={currentSection.styles?.maxWidth || 'full'}
                    onValueChange={(value) => handleStyleUpdate('maxWidth', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Pleine largeur</SelectItem>
                      <SelectItem value="1200px">1200px</SelectItem>
                      <SelectItem value="1024px">1024px</SelectItem>
                      <SelectItem value="768px">768px</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Alignement du texte</Label>
                  <Select 
                    value={currentSection.styles?.textAlign || 'left'}
                    onValueChange={(value) => handleStyleUpdate('textAlign', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Gauche</SelectItem>
                      <SelectItem value="center">Centre</SelectItem>
                      <SelectItem value="right">Droite</SelectItem>
                      <SelectItem value="justify">Justifié</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="effects" className="space-y-4">
                <div>
                  <Label>Ombre</Label>
                  <Select 
                    value={currentSection.styles?.boxShadow || 'none'}
                    onValueChange={(value) => handleStyleUpdate('boxShadow', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      <SelectItem value="0 1px 3px rgba(0,0,0,0.12)">Légère</SelectItem>
                      <SelectItem value="0 4px 6px rgba(0,0,0,0.1)">Moyenne</SelectItem>
                      <SelectItem value="0 10px 15px rgba(0,0,0,0.1)">Forte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Bordure arrondie (px)</Label>
                  <Slider
                    value={[parseInt(currentSection.styles?.borderRadius || '0')]}
                    onValueChange={([value]) => handleStyleUpdate('borderRadius', `${value}px`)}
                    min={0}
                    max={50}
                    step={2}
                  />
                  <span className="text-sm text-gray-500">
                    {currentSection.styles?.borderRadius || '0px'}
                  </span>
                </div>

                <div>
                  <Label>Opacité</Label>
                  <Slider
                    value={[parseFloat(currentSection.styles?.opacity || '1') * 100]}
                    onValueChange={([value]) => handleStyleUpdate('opacity', (value / 100).toString())}
                    min={0}
                    max={100}
                    step={5}
                  />
                  <span className="text-sm text-gray-500">
                    {Math.round(parseFloat(currentSection.styles?.opacity || '1') * 100)}%
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={currentSection.styles?.transform?.includes('hover') || false}
                    onCheckedChange={(checked) => 
                      handleStyleUpdate('hoverEffect', checked ? 'scale-105' : 'none')
                    }
                  />
                  <Label>Effet de survol</Label>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {/* Zone de prévisualisation */}
      <div className="flex-1 flex flex-col">
        {/* Barre d'outils */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant={viewport === 'desktop' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewport('desktop')}
            >
              <Monitor className="w-4 h-4" />
            </Button>
            <Button
              variant={viewport === 'tablet' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewport('tablet')}
            >
              <Tablet className="w-4 h-4" />
            </Button>
            <Button
              variant={viewport === 'mobile' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewport('mobile')}
            >
              <Smartphone className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Eye className="w-4 h-4 mr-2" />
              Aperçu
            </Button>
            <Button size="sm">
              <Save className="w-4 h-4 mr-2" />
              Publier
            </Button>
          </div>
        </div>

        {/* Zone de prévisualisation */}
        <div className="flex-1 bg-gray-100 p-8 overflow-auto">
          <div className={`mx-auto bg-white shadow-lg ${getViewportClasses()} transition-all duration-300`}>
            <div className="h-full overflow-auto">
              {sections?.map((section) => (
                <div
                  key={section.id}
                  className={`section-preview ${selectedSection === section.id ? 'ring-2 ring-blue-500' : ''} cursor-pointer`}
                  onClick={() => setSelectedSection(section.id)}
                  style={{
                    backgroundColor: section.background_color || 'transparent',
                    color: section.text_color || 'inherit',
                    padding: section.styles?.padding || '16px',
                    margin: section.styles?.margin || '0',
                    fontSize: section.styles?.fontSize || '16px',
                    fontFamily: section.styles?.fontFamily || 'Inter',
                    textAlign: section.styles?.textAlign as any || 'left',
                    maxWidth: section.styles?.maxWidth || '100%',
                    boxShadow: section.styles?.boxShadow || 'none',
                    borderRadius: section.styles?.borderRadius || '0',
                    opacity: section.styles?.opacity || '1',
                    transform: section.styles?.hoverEffect === 'scale-105' ? 'scale(1.05)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {section.title && (
                    <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                  )}
                  {section.subtitle && (
                    <h3 className="text-lg mb-4">{section.subtitle}</h3>
                  )}
                  {section.image_url && (
                    <img 
                      src={section.image_url} 
                      alt={section.title || 'Image'} 
                      className="mb-4 max-w-full h-auto"
                    />
                  )}
                  {section.content && (
                    <div className="prose prose-sm max-w-none">
                      {section.content.split('\n').map((paragraph, index) => (
                        <p key={index} className="mb-2">{paragraph}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
